import { PlatformSlug } from 'constants/consts'
import { AchievementTypeEnum, AwardTypeEnum, RewardTypeEnum } from '__generatedTypes__/globalTypes'

import { pluralize } from 'utils/pluralize'
import DailyWarmupLevel1 from './images/daily-warmup-level-1.png'
import DailyWarmupLevel2 from './images/daily-warmup-level-2.png'
import DailyWarmupLevel3 from './images/daily-warmup-level-3.png'
import HoursUsedLevel1 from './images/hours-used-level-1.png'
import HoursUsedLevel2 from './images/hours-used-level-2.png'
import HoursUsedLevel3 from './images/hours-used-level-3.png'
import PracticeTestLevel1 from './images/practice-test-level-1.png'
import PracticeTestLevel2 from './images/practice-test-level-2.png'
import PracticeTestLevel3 from './images/practice-test-level-3.png'
import PauseTokenLight from './Rewards/images/pause-token-light.png'
import PauseTokenDark from './Rewards/images/pause-token-dark.png'
import DayExtension1Light from './Rewards/images/day-extension-1-light.png'
import DayExtension1Dark from './Rewards/images/day-extension-1-dark.png'
import DayExtension2Light from './Rewards/images/day-extension-2-light.png'
import DayExtension2Dark from './Rewards/images/day-extension-2-dark.png'
import DayExtension3Light from './Rewards/images/day-extension-3-light.png'
import DayExtension3Dark from './Rewards/images/day-extension-3-dark.png'
import DayExtension4Light from './Rewards/images/day-extension-4-light.png'
import DayExtension4Dark from './Rewards/images/day-extension-4-dark.png'
import DayExtension5Light from './Rewards/images/day-extension-5-light.png'
import DayExtension5Dark from './Rewards/images/day-extension-5-dark.png'
import FreeMissedStreakLight from './Rewards/images/free-missed-streak-light.png'
import FreeMissedStreakDark from './Rewards/images/free-missed-streak-dark.png'
import MysteryBoxLight from './Rewards/images/mystery-box-light.png'
import MysteryBoxDark from './Rewards/images/mystery-box-dark.png'
import PrizeSelectorLight from './Rewards/images/prize-selector-light.png'
import PrizeSelectorDark from './Rewards/images/prize-selector-dark.png'

export const MAP_ENUM_TO_TITLE: Record<AchievementTypeEnum, string> = {
  [AchievementTypeEnum.DailyWarmup]: 'Daily Warmup Streaks',
  [AchievementTypeEnum.HoursUsed]: 'Hours Used',
  [AchievementTypeEnum.PracticeTest]: 'Practice Tests Completed',
  [AchievementTypeEnum.TestCompeted]: 'Test Completed',
  [AchievementTypeEnum.DailyGoal]: 'Daily Goal',
  [AchievementTypeEnum.LevelUpInEveryGame]: 'Olympian',
  [AchievementTypeEnum.LevelUpInReaderGame]: 'Speed Reader',
}

const HIDE_OPTIONS = [
  AchievementTypeEnum.DailyGoal,
  AchievementTypeEnum.LevelUpInReaderGame,
  AchievementTypeEnum.LevelUpInEveryGame,
]

export const TYPE_OPTIONS = Object.keys(AchievementTypeEnum)
  .filter((key) => !HIDE_OPTIONS.includes(key as AchievementTypeEnum))
  .map((key) => ({
    id: key,
    title: MAP_ENUM_TO_TITLE[key as AchievementTypeEnum],
  }))

export type AwardType = {
  id: string
  title: string
}

export const getAwardId = (awardType: AwardTypeEnum | null, awardTypeValue: number) => {
  if ([AwardTypeEnum.MembershipExtension, AwardTypeEnum.MysteryBox].includes(awardType as AwardTypeEnum)) {
    return `${awardType}-${awardTypeValue}`
  }

  return ''
}

export const getAwardTitle = (awardType: AwardTypeEnum | null, awardTypeValue: number) => {
  if (awardType === AwardTypeEnum.MembershipExtension) {
    return `+${awardTypeValue} Day Extension`
  }

  if (awardType === AwardTypeEnum.MysteryBox) {
    return `+${awardTypeValue} Mystery ${pluralize(awardTypeValue, 'Box', 'Boxes')}`
  }

  return ''
}

export const MEMBERSHIP_EXTENSION_AWARD_TYPE: Record<
  string,
  { awardType: AwardTypeEnum; awardTypeValue: number; title: string }
> = new Array(5).fill(0).reduce((acc, _, i) => {
  acc[getAwardId(AwardTypeEnum.MembershipExtension, i + 1)] = {
    awardType: AwardTypeEnum.MembershipExtension,
    awardTypeValue: i + 1,
    title: getAwardTitle(AwardTypeEnum.MembershipExtension, i + 1),
  }
  return acc
}, {})

export const MYSTERY_BOX_AWARD_TYPE: Record<
  string,
  { awardType: AwardTypeEnum; awardTypeValue: number; title: string }
> = new Array(5).fill(0).reduce((acc, _, i) => {
  acc[getAwardId(AwardTypeEnum.MysteryBox, i + 1)] = {
    awardType: AwardTypeEnum.MysteryBox,
    awardTypeValue: i + 1,
    title: getAwardTitle(AwardTypeEnum.MysteryBox, i + 1),
  }
  return acc
}, {})

export const MEMBERSHIP_EXTENSION_AWARD_TYPE_OPTIONS = Object.keys(MEMBERSHIP_EXTENSION_AWARD_TYPE).map((key) => ({
  id: key,
  title: MEMBERSHIP_EXTENSION_AWARD_TYPE[key as keyof typeof MEMBERSHIP_EXTENSION_AWARD_TYPE].title,
}))

export const MYSTERY_BOX_AWARD_TYPE_OPTIONS = Object.keys(MYSTERY_BOX_AWARD_TYPE).map((key) => ({
  id: key,
  title: MYSTERY_BOX_AWARD_TYPE[key as keyof typeof MYSTERY_BOX_AWARD_TYPE].title,
}))

export const LEVELS = 3

export const getLevelTitle = (level: number) => `Level ${level}`

export const LEVEL_OPTIONS = new Array(LEVELS).fill(0).map((_, i) => ({
  id: `${i + 1}`,
  title: getLevelTitle(i + 1),
}))

export const LEVEL_IMAGE_MAP: Record<AchievementTypeEnum, Record<string, typeof DailyWarmupLevel1>> = {
  [AchievementTypeEnum.DailyWarmup]: {
    1: DailyWarmupLevel1,
    2: DailyWarmupLevel2,
    3: DailyWarmupLevel3,
  },
  [AchievementTypeEnum.HoursUsed]: {
    1: HoursUsedLevel1,
    2: HoursUsedLevel2,
    3: HoursUsedLevel3,
  },
  [AchievementTypeEnum.PracticeTest]: {
    1: PracticeTestLevel1,
    2: PracticeTestLevel2,
    3: PracticeTestLevel3,
  },
  [AchievementTypeEnum.TestCompeted]: {
    1: PracticeTestLevel1,
    2: PracticeTestLevel2,
    3: PracticeTestLevel3,
  },
  [AchievementTypeEnum.DailyGoal]: {},
  [AchievementTypeEnum.LevelUpInReaderGame]: {},
  [AchievementTypeEnum.LevelUpInEveryGame]: {},
}

enum MembershipExtensionEnum {
  MembershipExtension1 = 'MembershipExtension-1',
  MembershipExtension2 = 'MembershipExtension-2',
  MembershipExtension3 = 'MembershipExtension-3',
  MembershipExtension4 = 'MembershipExtension-4',
  MembershipExtension5 = 'MembershipExtension-5',
}

export type RewardImageMode = 'light' | 'dark'

type RewardImageEntry = { mode: RewardImageMode; src: string }

type RewardImageValue = RewardImageEntry[] | Record<string, RewardImageEntry[]>

export const REWARD_IMAGE_MAP: Record<RewardTypeEnum, RewardImageValue> = {
  [RewardTypeEnum.Extension]: {
    [MembershipExtensionEnum.MembershipExtension1]: [
      { mode: 'light', src: DayExtension1Light },
      { mode: 'dark', src: DayExtension1Dark },
    ],
    [MembershipExtensionEnum.MembershipExtension2]: [
      { mode: 'light', src: DayExtension2Light },
      { mode: 'dark', src: DayExtension2Dark },
    ],
    [MembershipExtensionEnum.MembershipExtension3]: [
      { mode: 'light', src: DayExtension3Light },
      { mode: 'dark', src: DayExtension3Dark },
    ],
    [MembershipExtensionEnum.MembershipExtension4]: [
      { mode: 'light', src: DayExtension4Light },
      { mode: 'dark', src: DayExtension4Dark },
    ],
    [MembershipExtensionEnum.MembershipExtension5]: [
      { mode: 'light', src: DayExtension5Light },
      { mode: 'dark', src: DayExtension5Dark },
    ],
  },
  [RewardTypeEnum.FreeMissedStreak]: [
    { mode: 'light', src: FreeMissedStreakLight },
    { mode: 'dark', src: FreeMissedStreakDark },
  ],
  [RewardTypeEnum.PauseToken]: [
    { mode: 'light', src: PauseTokenLight },
    { mode: 'dark', src: PauseTokenDark },
  ],
  [RewardTypeEnum.PrizeSelector]: [
    { mode: 'light', src: PrizeSelectorLight },
    { mode: 'dark', src: PrizeSelectorDark },
  ],
  [RewardTypeEnum.MysteryBox]: [
    { mode: 'light', src: MysteryBoxLight },
    { mode: 'dark', src: MysteryBoxDark },
  ],
}

export const getRewardImageSrc = (rewardType: RewardTypeEnum, mode: RewardImageMode, subType?: string) => {
  const entry = REWARD_IMAGE_MAP[rewardType]
  const list = Array.isArray(entry) ? entry : subType ? entry[subType] : undefined

  return list?.find((item) => item.mode === mode)?.src
}

export const hasRewardImage = (rewardType: RewardTypeEnum, subType?: string) => {
  const entry = REWARD_IMAGE_MAP[rewardType]

  if (Array.isArray(entry)) {
    return entry.length > 0
  }

  return !!subType && (entry[subType]?.length ?? 0) > 0
}

export const getAvailableAchievementTypes = (platform: PlatformSlug) => {
  if ([PlatformSlug.INBDEBooster, PlatformSlug.NBDHEBooster, PlatformSlug.NDEBCrusher].includes(platform)) {
    return [TYPE_OPTIONS[0], TYPE_OPTIONS[1]]
  }

  if (platform === PlatformSlug.CasperBooster) {
    return [TYPE_OPTIONS[0], TYPE_OPTIONS[3]]
  }

  return TYPE_OPTIONS.filter(({ id }) => id !== AchievementTypeEnum.TestCompeted)
}

export const getAwardTypeOptions = (platform: PlatformSlug) => {
  if (platform === PlatformSlug.CARSBooster) {
    return MYSTERY_BOX_AWARD_TYPE_OPTIONS.slice(0, 1)
  }

  if (platform === PlatformSlug.CasperBooster) {
    return MEMBERSHIP_EXTENSION_AWARD_TYPE_OPTIONS.slice(0, 2)
  }

  return MEMBERSHIP_EXTENSION_AWARD_TYPE_OPTIONS
}

export const getExtensionOptions = (platform: PlatformSlug) => {
  if (platform === PlatformSlug.CasperBooster) {
    return MEMBERSHIP_EXTENSION_AWARD_TYPE_OPTIONS.slice(0, 3)
  }

  return MEMBERSHIP_EXTENSION_AWARD_TYPE_OPTIONS
}
